import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import useModalEffect from '../../../hooks/useModalEffect';

const CreateRoleForm = ({ isOpen, onClose, onSubmit, initialData }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState(
    initialData || {
      nameRole: '',
      description: ''
    }
  );
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (initialData) {
      let roleName = initialData.nameRole || initialData.name || '';
      // Ensure ROLE_ prefix for existing data
      if (roleName && !roleName.startsWith('ROLE_')) {
        roleName = 'ROLE_' + roleName;
      }
      setFormData({
        nameRole: roleName,
        description: initialData.description || ''
      });
    } else {
      setFormData({ nameRole: 'ROLE_', description: '' });
    }
  }, [initialData, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for nameRole to ensure ROLE_ prefix
    if (name === 'nameRole') {
      let formattedValue = value.toUpperCase();
      
      // If user tries to delete ROLE_ prefix, restore it
      if (!formattedValue.startsWith('ROLE_')) {
        // If value is empty or doesn't start with ROLE_, add it
        if (formattedValue === '' || formattedValue.length < 5) {
          formattedValue = 'ROLE_';
        } else {
          // If user typed something without ROLE_, prepend it
          formattedValue = 'ROLE_' + formattedValue.replace(/^ROLE_*/i, '');
        }
      }
      
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.nameRole.trim()) {
      errors.nameRole = 'Role name is required';
    } else if (!formData.nameRole.startsWith('ROLE_')) {
      errors.nameRole = 'Role name must start with ROLE_';
    } else if (formData.nameRole.trim() === 'ROLE_') {
      errors.nameRole = 'Please enter a role name after ROLE_';
    } else if (formData.nameRole.length < 6) {
      errors.nameRole = 'Role name is too short';
    }
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    return errors;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true);
      setSubmitError('');
      try {
        // Format data for backend - backend expects 'name' field
        const backendData = {
          name: formData.nameRole,
          description: formData.description
        };
        await onSubmit(backendData);
        // Only reset form if submission was successful
        // The parent component will close the modal
        if (!initialData) {
          setFormData({ nameRole: 'ROLE_', description: '' });
        }
      } catch (error) {
        // Handle error - show error message but keep modal open
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to save role. Please try again.';
        setSubmitError(errorMessage);
        console.error('Error saving role:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setFormErrors(errors);
    }
  };

  const handleModalClose = () => {
    setFormData({ nameRole: 'ROLE_', description: '' });
    setFormErrors({});
    setSubmitError('');
    setIsSubmitting(false);
    onClose();
  };

  return (
    <>
      <style>{`
        #modal-create-role .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-role .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-create-role .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-create-role .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-role .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-create-role .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      <div id="modal-create-role" className={`modal modal-blur fade ${isOpen ? 'show' : ''}`}
           style={{ display: isOpen ? 'block' : 'none' }}
           tabIndex="-1"
           role="dialog">
        <div className="modal-dialog modal-md modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{initialData ? 'Edit Role' : 'Create Role'}</h5>
              <button type="button" className="btn-close" onClick={handleModalClose}></button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="inv-section-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  Details
                </div>
                <div className="mb-3">
                  <label className="form-label">Role Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.nameRole ? 'is-invalid' : ''}`}
                    name="nameRole"
                    value={formData.nameRole}
                    onChange={handleInputChange}
                    placeholder="ROLE_ADMIN"
                    style={{ fontFamily: 'monospace' }}
                  />
                  {formErrors.nameRole && (
                    <div className="invalid-feedback">{formErrors.nameRole}</div>
                  )}
                  <small className="form-hint text-muted">
                    Role name must start with <strong>ROLE_</strong> followed by the role type in uppercase (e.g., ROLE_ADMIN, ROLE_MANAGER, ROLE_SALES)
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Description <span className="text-danger">*</span></label>
                  <textarea
                    className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter description"
                    rows={4}
                  />
                  {formErrors.description && (
                    <div className="invalid-feedback">{formErrors.description}</div>
                  )}
                </div>
                {submitError && (
                  <div className="alert alert-danger" role="alert">
                    {submitError}
                  </div>
                )}
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  {isSubmitting ? 'Saving…' : (
                    <span style={{ color: '#9aa5b1' }}>
                      <span className="inv-required-star">*</span> Required fields
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleModalClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-inv-save"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
                      Saving…
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      Save
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
    </>
  );
};

CreateRoleForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object
};

export default CreateRoleForm;
